/**
 * Mobile-first Admin landing + nested admin sub-routes. Only operators with is_admin=true.
 */
import { Link, Navigate, Routes, Route } from "react-router-dom";
import { useOperator } from "@/lib/operator";
import OperatorHeader from "@/components/OperatorHeader";
import { Users, MapPin, BookOpen, Tractor, Cloud, ChevronRight } from "lucide-react";
import {
  AdminOperators, AdminFields, AdminPassingPlaces, AdminDiary, AdminMachines, AdminIntegrations
} from "./OperatorAdminPages";

const TILES = [
  { to: "/app/admin/operators",      icon: Users,    title: "Operators",      sub: "Add / rename / set PIN / admin" },
  { to: "/app/admin/fields",         icon: MapPin,   title: "Fields",         sub: "Per-customer field setup with map" },
  { to: "/app/admin/passing-places", icon: MapPin,   title: "Passing places", sub: "Set up shared road points" },
  { to: "/app/admin/diary",          icon: BookOpen, title: "Diary review",   sub: "Everyone's entries, mark invoiced" },
  { to: "/app/admin/machines",       icon: Tractor,  title: "Machines",       sub: "Fleet list" },
  { to: "/app/admin/integrations",   icon: Cloud,    title: "Integrations",   sub: "Xero + FieldOps status" },
];

function AdminLanding() {
  const { operator } = useOperator();
  return (
    <div className="min-h-screen bg-slate-50">
      <OperatorHeader back="/app" title="Admin" />
      <div className="px-4 sm:px-6 py-6 max-w-3xl mx-auto space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#c41e3a] font-bold">Wilson Contractors</div>
          <h1 className="font-display font-black text-2xl text-[#1a1a5c]">Admin · {operator.name}</h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure customers, fields, operators and integrations — everything that needs setting
            up before crew can use the Field App.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TILES.map(({ to, icon: Icon, title, sub }) => (
            <Link key={to} to={to} data-testid={`admin-tile-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-3 bg-white hover:bg-amber-50 border border-slate-200 hover:border-amber-300 rounded-xl px-4 py-3 active:scale-[0.99] transition-all shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#1a1a5c] text-white grid place-items-center shrink-0">
                <Icon size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-[#1a1a5c]">{title}</div>
                <div className="text-[11px] text-slate-500 truncate">{sub}</div>
              </div>
              <ChevronRight size={16} className="text-slate-400 shrink-0" />
            </Link>
          ))}
        </div>
        <div className="text-[11px] text-slate-400 text-center pt-2">
          For complex tasks (FieldOps OAuth, Xero connect…) the desktop console at{" "}
          <a href="/admin" className="underline">/admin</a> still works.
        </div>
      </div>
    </div>
  );
}

export default function OperatorAdmin() {
  const { operator } = useOperator();
  if (!operator?.is_admin) return <Navigate to="/app" replace />;
  return (
    <Routes>
      <Route index                element={<AdminLanding />} />
      <Route path="operators"     element={<AdminOperators />} />
      <Route path="fields"        element={<AdminFields />} />
      <Route path="passing-places" element={<AdminPassingPlaces />} />
      <Route path="diary"         element={<AdminDiary />} />
      <Route path="machines"      element={<AdminMachines />} />
      <Route path="integrations"  element={<AdminIntegrations />} />
    </Routes>
  );
}
